import { useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { apiFetch } from '../../config/api'

export default function DashboardLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [dbTestResult, setDbTestResult] = useState<string | null>(null)
  const [isTestingDb, setIsTestingDb] = useState(false)
  const navigate = useNavigate()

  // Bereits eingeloggt → direkt ins Dashboard
  if (localStorage.getItem('dashboard_token')) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email || !password) {
      setError('Bitte füllen Sie alle Felder aus.')
      return
    }

    try {
      const response = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        localStorage.setItem('dashboard_token', data.token)
        localStorage.setItem('dashboard_user', JSON.stringify(data.user))
        navigate('/dashboard')
      } else {
        setError(data.error || 'Anmeldung fehlgeschlagen')
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuchen Sie es erneut.')
      console.error('Login error:', err)
    }
  }

  const testDatabaseConnection = async () => {
    setIsTestingDb(true)
    setDbTestResult(null)
    setError('')

    try {
      // Versuche zuerst direkt test-db.php
      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const testUrl = apiBase.startsWith('http') 
        ? `${apiBase}/test-db.php`
        : `${apiBase}/test-db.php`
      
      console.log('Teste URL:', testUrl)
      
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      console.log('Response Status:', response.status)
      console.log('Response URL:', response.url)

      // Prüfe ob Response Text ist (PHP-Fehler)
      const responseText = await response.text()
      
      // Wenn HTML/PHP-Code zurückkommt, ist PHP nicht ausgeführt worden
      if (responseText.trim().startsWith('<?php') || responseText.trim().startsWith('<!')) {
        throw new Error(`PHP wird nicht ausgeführt. Die Datei wird direkt geladen statt über den Router.\n\nAntwort (erste 300 Zeichen):\n${responseText.substring(0, 300)}`)
      }
      
      let data
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        throw new Error(`Ungültige JSON-Antwort: ${responseText.substring(0, 200)}`)
      }

      if (data.success) {
        const result = `
✅ Verbindung erfolgreich!

Datenbank: ${data.database}
Host: ${data.host}

Tabellen:
${Object.entries(data.tables).map(([table, info]: [string, any]) => 
  `  ${table}: ${info.exists ? `✅ ${info.count} Einträge` : '❌ Nicht gefunden'}`
).join('\n')}

Admin-User: ${data.admin_user_exists ? '✅ Existiert' : '❌ Nicht gefunden'}
${data.admin_user ? `  ID: ${data.admin_user.id}\n  E-Mail: ${data.admin_user.email}` : ''}
        `.trim()
        setDbTestResult(result)
      } else {
        setDbTestResult(`❌ Fehler: ${data.message || data.error}\n\nDetails:\n${JSON.stringify(data, null, 2)}`)
      }
    } catch (err: any) {
      // Versuche direkten Test-Endpoint
      try {
        const testResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/test-connection.php`)
        const testText = await testResponse.text()
        if (testText.includes('success')) {
          const testData = JSON.parse(testText)
          setDbTestResult(`✅ API ist erreichbar, aber /test-db Endpoint nicht gefunden.\n\nAPI Info:\n${JSON.stringify(testData, null, 2)}\n\nMögliche Ursachen:\n- Router.php leitet nicht richtig weiter\n- test-db.php fehlt im api/ Ordner\n- .htaccess Konfiguration`)
        } else {
          throw new Error('Test-Endpoint auch nicht erreichbar')
        }
      } catch (testErr) {
        setDbTestResult(`❌ Verbindungsfehler: ${err.message || 'Unbekannter Fehler'}\n\nAufgerufene URL: ${err.url || 'unbekannt'}\n\nBitte prüfe:\n- Ist die API-URL korrekt? (aktuell: ${import.meta.env.VITE_API_BASE_URL || '/api'})\n- Ist die .htaccess korrekt?\n- Läuft der Backend-Server?\n- Ist router.php vorhanden?`)
      }
      console.error('DB Test error:', err)
    } finally {
      setIsTestingDb(false)
    }
  }

  return (
    <div className="dashboard-app flex min-h-svh items-center justify-center bg-zinc-100 px-4 py-10 font-sans text-zinc-950">
      <div className="w-full max-w-sm overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 bg-zinc-950 px-5 py-4">
          <img src="/Logo_HM.png" alt="Hückels May" className="h-10 w-auto" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">Hückels May</p>
            <p className="truncate text-xs text-zinc-400">Verwaltung</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-zinc-950">Anmelden</h1>
            <p className="mt-1 text-sm text-zinc-500">Zugang zur Verwaltung.</p>
          </div>

          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-zinc-700">
              E-Mail
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-dashboard"
              placeholder="ihre@email.de"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-zinc-700">
              Passwort
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-dashboard"
              placeholder="Passwort"
              required
            />
          </div>

          {error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          )}

          <button
            type="submit"
            className="inline-flex h-9 w-full items-center justify-center rounded-md bg-zinc-900 text-sm font-medium text-white hover:bg-zinc-800"
          >
            Anmelden
          </button>
        </form>

        <div className="border-t border-zinc-200 px-5 py-4">
          <button
            type="button"
            onClick={testDatabaseConnection}
            disabled={isTestingDb}
            className="text-sm font-medium text-zinc-500 hover:text-zinc-800 disabled:opacity-50"
          >
            {isTestingDb ? 'Verbindung wird geprüft…' : 'Datenbankverbindung prüfen'}
          </button>
          {dbTestResult && (
            <pre className="mt-3 max-h-64 overflow-auto rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs leading-relaxed whitespace-pre-wrap text-zinc-700">
              {dbTestResult}
            </pre>
          )}
        </div>
      </div>
    </div>
  )
}
















